<app-dataspace-report-workspace-layout
  [title]="'Federated Catalog Browser'"
  [subtitle]="'Dynamisch aufgelöste Data Offers im Dataspace durchsuchen'"
  [stats]="stats">

  <div workspace-sidebar class="dataspace-pane">
    <mat-card class="dataspace-filter-card">
      <div class="dataspace-filter-grid">
        <mat-form-field appearance="outline">
          <mat-label>Katalog durchsuchen (Projekt / NU)</mat-label>
          <input matInput [(ngModel)]="catalogSearchQuery" (keyup.enter)="loadCatalog()">
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>

        <button mat-raised-button color="primary" (click)="loadCatalog()">
          <mat-icon>refresh</mat-icon>
          Katalog aktualisieren
        </button>
      </div>
    </mat-card>

    <mat-progress-bar mode="indeterminate" *ngIf="loading"></mat-progress-bar>

    <div class="dataspace-list" *ngIf="!loading && items.length">
      <button
        type="button"
        class="dataspace-list-item"
        *ngFor="let item of items; trackBy: trackByAssetId"
        [class.is-selected]="selectedItem?.offerId === item.offerId"
        (click)="selectItem(item)">
        <div class="dataspace-list-item__meta">
          <span class="dataspace-list-item__producer">{{ item.producerOrgName }}</span>
          <span class="dataspace-list-item__date" *ngIf="item.lastReportDate">{{ item.lastReportDate }}</span>
        </div>
        <div class="dataspace-list-item__title-row">
          <strong>{{ item.title }}</strong>
          <button
            *ngIf="item.includedSections?.length"
            type="button"
            class="expand-toggle"
            (click)="toggleItemExpand(item, $event)"
            [attr.aria-label]="expandedOfferId === item.offerId ? 'Abschnitte einklappen' : 'Abschnitte aufklappen'">
            <mat-icon>{{ expandedOfferId === item.offerId ? 'expand_less' : 'folder_open' }}</mat-icon>
          </button>
        </div>
        <span class="dataspace-list-item__policy">{{ item.policySummary }}</span>
        <div class="section-pills" *ngIf="expandedOfferId === item.offerId && item.includedSections?.length">
          <span class="section-pill" *ngFor="let sid of item.includedSections">{{ prettySection(sid) }}</span>
        </div>
        <div class="dataspace-list-item__footer">
          <span class="catalog-offer-badge">{{ item.reportCount }} Berichte</span>
          <small>{{ item.projectLabels?.length || 0 }} Projekte</small>
        </div>
      </button>
    </div>

    <mat-card class="dataspace-empty-state" *ngIf="!loading && !items.length">
      <mat-icon>search</mat-icon>
      <strong>Keine passenden Angebote</strong>
      <span>Verwenden Sie die Suche, um dynamische Data Offers im Federated Catalog zu finden.</span>
    </mat-card>
  </div>

  <div workspace-detail class="dataspace-detail" *ngIf="selectedItem; else guEmptySelection">
    <mat-card class="dataspace-detail__hero">
      <div>
        <div class="dataspace-detail__eyebrow">Angebot Details</div>
        <h3>{{ selectedItem.title }}</h3>
        <p>{{ selectedItem.producerOrgName }} · {{ selectedItem.reportCount }} Berichte</p>
      </div>
      <span class="status-badge" [class]="statusBadgeClass">{{ statusBadgeLabel }}</span>
    </mat-card>

    <!-- Summary Row: Policy (always visible) + Sovereignty -->
    <div class="dataspace-summary-row">
      <mat-card class="dataspace-summary-card">
        <div class="policy-oneliner">
          <mat-icon class="policy-oneliner__icon">policy</mat-icon>
          <span class="policy-oneliner__text">{{ policyOneLiner }}</span>
          <mat-icon class="policy-oneliner__chevron"
                    (click)="policyExpanded = !policyExpanded"
                    *ngIf="detail">{{ policyExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
        </div>
        <button mat-raised-button color="primary" class="buy-button"
                *ngIf="effectiveDetail?.paymentRequired && !effectiveDetail?.paymentGranted"
                (click)="openPurchaseDialog()">
          <mat-icon>shopping_cart</mat-icon> Kaufen
        </button>
        <div class="policy-oneliner__detail" *ngIf="policyExpanded && detail">
          <ul *ngIf="policyFacts.length">
            <li *ngFor="let line of policyFacts">{{ line }}</li>
          </ul>
        </div>
      </mat-card>

      <mat-card class="dataspace-summary-card sovereignty-card">
        <div class="sovereignty-icon-row">
          <div class="sovereignty-icon"
               [class.sovereignty-icon--active]="effectiveDetail?.contentAccessible"
               [class.sovereignty-icon--disabled]="!effectiveDetail?.contentAccessible"
               [matTooltip]="securedViewerTooltip">
            <mat-icon>visibility</mat-icon>
          </div>
          <div class="sovereignty-icon"
               [class.sovereignty-icon--active]="effectiveDetail?.usagePolicy?.mode === 'compute-to-data'"
               [class.sovereignty-icon--disabled]="effectiveDetail?.usagePolicy?.mode !== 'compute-to-data'"
               [matTooltip]="computeToDataTooltip">
            <mat-icon>memory</mat-icon>
          </div>
          <div class="sovereignty-icon"
               [class.sovereignty-icon--active]="effectiveDetail?.exportAllowed"
               [class.sovereignty-icon--disabled]="!effectiveDetail?.exportAllowed"
               [matTooltip]="downloadTooltip">
            <mat-icon>download_for_offline</mat-icon>
          </div>
        </div>
        <div class="sovereignty-export-buttons" *ngIf="effectiveDetail?.exportAllowed">
          <button mat-stroked-button type="button" [disabled]="exportBusy || !effectiveDetail?.usagePolicy?.permissions?.includes('EXPORT_PDF')" (click)="exportOffer('PDF')">PDF</button>
          <button mat-stroked-button type="button" [disabled]="exportBusy || !effectiveDetail?.usagePolicy?.permissions?.includes('EXPORT_XML')" (click)="exportOffer('XML')">XML</button>
        </div>
      </mat-card>
    </div>

    <!-- Offer Contents Dashboard -->
    <div class="offer-dashboard" *ngIf="detail">
      <div class="dashboard-kpi-row">
        <div class="dashboard-kpi">
          <mat-icon>description</mat-icon>
          <div class="dashboard-kpi__content">
            <span class="dashboard-kpi__value">{{ detail.resolvedReportCount || 0 }}</span>
            <span class="dashboard-kpi__label">Berichte</span>
          </div>
        </div>
        <div class="dashboard-kpi">
          <mat-icon>folder</mat-icon>
          <div class="dashboard-kpi__content">
            <span class="dashboard-kpi__value">{{ detail.projectLabels?.length || 0 }}</span>
            <span class="dashboard-kpi__label">Projekte</span>
          </div>
        </div>
        <div class="dashboard-kpi">
          <mat-icon>euro</mat-icon>
          <div class="dashboard-kpi__content">
            <span class="dashboard-kpi__value">{{ dashboardPricingLabel }}</span>
            <span class="dashboard-kpi__label">Preis</span>
          </div>
        </div>
        <div class="dashboard-kpi">
          <mat-icon>date_range</mat-icon>
          <div class="dashboard-kpi__content">
            <span class="dashboard-kpi__value">{{ dashboardReportDateRange }}</span>
            <span class="dashboard-kpi__label">Berichtszeitraum</span>
          </div>
        </div>
      </div>

      <div class="dashboard-charts-row" *ngIf="detail.resolvedReports?.length">
        <div class="dashboard-chart-card" *ngIf="dashboardReportsPerProject.length">
          <h5>Berichte pro Projekt</h5>
          <div class="chart-with-legend">
            <ngx-charts-pie-chart
              [results]="dashboardReportsPerProject"
              [scheme]="dashboardColorScheme"
              [doughnut]="true"
              [labels]="false"
              [legend]="false"
              [view]="[340, 180]">
            </ngx-charts-pie-chart>
            <div class="chart-legend-row">
              <span class="chart-legend-item" *ngFor="let item of dashboardReportsPerProject; let i = index">
                <span class="chart-legend-swatch" [style.background]="dashboardColorScheme.domain[i % dashboardColorScheme.domain.length]"></span>
                {{ item.name }} ({{ item.value }})
              </span>
            </div>
          </div>
        </div>

        <div class="dashboard-chart-card" *ngIf="dashboardStatusBreakdown.length > 1">
          <h5>Status-Verteilung</h5>
          <ngx-charts-pie-chart
            [results]="dashboardStatusBreakdown"
            [scheme]="dashboardColorScheme"
            [doughnut]="true"
            [view]="[380, 220]">
          </ngx-charts-pie-chart>
        </div>

        <div class="dashboard-chart-card" *ngIf="dashboardSectionCoverage.length">
          <h5>Abschnitte (Abdeckung)</h5>
          <ngx-charts-bar-horizontal
            [results]="dashboardSectionCoverage"
            [scheme]="dashboardColorScheme"
            [xAxisLabel]="'Berichte'"
            [yAxisLabel]="'Abschnitt'"
            [showXAxisLabel]="true"
            [showYAxisLabel]="true"
            [xAxis]="true"
            [yAxis]="true"
            [view]="[380, 220]">
          </ngx-charts-bar-horizontal>
        </div>
      </div>
    </div>

    <div class="data-access-section">
       <mat-divider class="section-divider"></mat-divider>
       <div class="data-access-header">
         <h4>Datenzugriff (Data Plane)</h4>
       </div>
       <app-gu-data-access [detail]="detail"></app-gu-data-access>
    </div>

    <mat-card class="dataspace-error-card" *ngIf="error">
      <mat-icon>error</mat-icon>
      <span>{{ error }}</span>
    </mat-card>
  </div>
</app-dataspace-report-workspace-layout>

<ng-template #guEmptySelection>
  <mat-card class="dataspace-empty-state">
    <mat-icon>manage_search</mat-icon>
    <strong>Katalog durchsuchen</strong>
    <span>Wählen Sie links ein Angebot aus, um die Vertragsbedingungen zu prüfen.</span>
  </mat-card>
</ng-template>
